import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
  TextField,
} from '@mui/material';
import {
  AccountBalanceWallet as CarteraIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  LockOutlined as LockOutlinedIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpInputSx } from 'components/financepro';
import PrintListado, { escapeHtml } from 'utils/PrintListado';
import VistaFactura from '../facturapos/VistaFactura';

const PROGRAMA_CUENTAS_POR_COBRAR = 'cuentasxcobrar';

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  try {
    const d = new Date(fecha);
    return d.toLocaleDateString('es-DO', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return fecha;
  }
};

const tramoLabel = (t) => {
  if (t === '90+') return 'Más de 90 días';
  return `${t} días`;
};

const tramoColor = (t) => {
  if (t === '90+') return { bg: FP.color.dangerSoft, fg: FP.color.danger };
  if (t === '61-90') return { bg: FP.color.warningSoft, fg: '#92400E' };
  if (t === '31-60') return { bg: FP.color.accentBlueSoft, fg: FP.color.accentBlue };
  return { bg: FP.color.successSoft, fg: FP.color.successDark };
};

const nombreClienteCxC = (f) => (f?.nombre_cliente || f?.suplidor || '').trim() || '—';

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

/**
 * Listado de cuentas por cobrar por antigüedad. Permite filtrar por fecha de
 * emisión, ver tramos 0-30/31-60/61-90/90+, reimprimir factura y exportar a
 * impresión. Solo lectura: el guard de seguridad es `consultacuentasxcobrar`.
 */
const CuentasPorCobrar = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_CUENTAS_POR_COBRAR),
    [roleDetalles]
  );

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [fechaCorteAntiguedad, setFechaCorteAntiguedad] = useState(null);
  const [vistaFacturaAbierta, setVistaFacturaAbierta] = useState(false);
  const [datosVistaFactura, setDatosVistaFactura] = useState({});

  const fetchList = useCallback(
    async (desde = '', hasta = '') => {
      if (!puedeLeer || !selectedCompany?.db_empresa) return;
      const d = (desde || '').trim();
      const h = (hasta || '').trim();
      if (d && h && d > h) {
        showError('La fecha desde no puede ser posterior a la fecha hasta');
        return;
      }
      setCargando(true);
      try {
        const body = {
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        };
        if (d) body.fecha_desde = d;
        if (h) body.fecha_hasta = h;
        const response = await fetch(servidor + '/api/facturabackend_listarcuentasxcobrar', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (response.ok) {
          const lista = data.facturas || [];
          setFilas(lista);
          setFechaCorteAntiguedad(data.fecha_corte_antiguedad || null);
          if (lista.length === 0) {
            showSuccess('No hay facturas a crédito con saldo pendiente para los filtros indicados');
          }
        } else {
          showError(data.Error || 'Error al cargar cuentas por cobrar');
          setFilas([]);
          setFechaCorteAntiguedad(null);
        }
      } catch {
        showError('Error de conexión al cargar cuentas por cobrar');
        setFilas([]);
        setFechaCorteAntiguedad(null);
      } finally {
        setCargando(false);
      }
    },
    [puedeLeer, selectedCompany, usuarioLog, claveLog, servidor, showError, showSuccess]
  );

  useEffect(() => {
    fetchList('', '');
  }, [fetchList]);

  const handleBuscar = () => fetchList(fechaDesde, fechaHasta);

  const handleLimpiarFiltros = () => {
    setFechaDesde('');
    setFechaHasta('');
    fetchList('', '');
  };

  const ordenadasAntiguedad = useMemo(
    () => [...filas].sort((a, b) => (b.dias_antiguedad || 0) - (a.dias_antiguedad || 0)),
    [filas]
  );

  const tarjetas = useMemo(() => {
    const tramos = ['0-30', '31-60', '61-90', '90+'];
    return tramos.map((tramo) => {
      const enTramo = filas.filter((f) => f.tramo_antiguedad === tramo);
      const suma = enTramo.reduce((s, f) => s + (parseFloat(f.saldo_pendiente) || 0), 0);
      return { tramo, cantidad: enTramo.length, suma };
    });
  }, [filas]);

  const totalCartera = useMemo(
    () => filas.reduce((s, f) => s + (parseFloat(f.saldo_pendiente) || 0), 0),
    [filas]
  );

  const footerPrint = useMemo(() => {
    const celdasTramo = tarjetas
      .map(({ tramo, suma, cantidad }) => {
        const n = cantidad === 1 ? '1 factura' : `${cantidad} facturas`;
        return (
          `<td>` +
          `<div class="kpi-label">${escapeHtml(tramoLabel(tramo))}</div>` +
          `<div class="kpi-value">$${escapeHtml(fmtMoney(suma))}</div>` +
          `<div class="kpi-hint">${escapeHtml(n)}</div>` +
          `</td>`
        );
      })
      .join('');
    const nDocs = filas.length === 1 ? '1 documento' : `${filas.length} documentos`;
    const celdaTotal =
      `<td class="kpi-emphasis">` +
      `<div class="kpi-label">Total cartera</div>` +
      `<div class="kpi-value">$${escapeHtml(fmtMoney(totalCartera))}</div>` +
      `<div class="kpi-hint">${escapeHtml(nDocs)}</div>` +
      `</td>`;
    return (
      `<p class="print-footer-title">Resumen de antigüedad de saldo</p>` +
      `<table class="print-kpis"><tr>${celdasTramo}${celdaTotal}</tr></table>`
    );
  }, [tarjetas, totalCartera, filas.length]);

  const reimprimirFactura = async (nfact, tipofactura) => {
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerfactura', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          nfact,
          tipofactura,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const factura = data.factura || {};
        setDatosVistaFactura({
          ...factura,
          id_empresa: selectedCompany,
        });
        setVistaFacturaAbierta(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al obtener la factura');
      }
    } catch {
      showError('Error de conexión al obtener la factura');
    }
  };

  const columnasPrint = [
    { id: 'tramo_antiguedad', label: 'Tramo', align: 'center' },
    { id: 'dias_antiguedad', label: 'Días', align: 'center' },
    { id: 'tipofactura', label: 'Documento' },
    { id: 'nfact', label: 'Nº' },
    { id: 'fecha', label: 'Fecha', align: 'center', render: (_, row) => formatearFecha(row.fecha) },
    {
      id: 'nombre_cliente',
      label: 'Cliente',
      render: (_, row) => nombreClienteCxC(row),
    },
    { id: 'total', label: 'Total', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    { id: 'valorpagado', label: 'Pagado', align: 'right', render: (v) => `$${fmtMoney(v)}` },
    {
      id: 'total_notas_credito',
      label: 'N/C',
      align: 'right',
      render: (v) => `$${fmtMoney(v)}`,
    },
    {
      id: 'total_notas_debito',
      label: 'N/D',
      align: 'right',
      render: (v) => `$${fmtMoney(v)}`,
    },
    {
      id: 'saldo_pendiente',
      label: 'Saldo pendiente',
      align: 'right',
      render: (v) => `$${fmtMoney(v)}`,
    },
    { id: 'ncf', label: 'NCF', align: 'center', render: (_, row) => row.ncf || '-' },
  ];

  const subtituloReporte = useMemo(() => {
    const partes = [];
    if (fechaDesde) partes.push(`Emisión desde ${formatearFecha(fechaDesde)}`);
    if (fechaHasta) partes.push(`Emisión hasta ${formatearFecha(fechaHasta)}`);
    const rango = partes.length ? partes.join(' · ') : 'Todas las fechas de emisión';
    const corte =
      fechaCorteAntiguedad != null ? ` · Antigüedad al ${formatearFecha(fechaCorteAntiguedad)}` : '';
    return `${rango} · ${filas.length} documento(s)${corte}`;
  }, [fechaDesde, fechaHasta, fechaCorteAntiguedad, filas.length]);

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Cuentas por cobrar"
          subtitle={nombreNegocio ? `${nombreNegocio} · Antigüedad de cartera` : 'Antigüedad de cartera'}
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              ...cardShellSx,
              maxWidth: 560,
              width: '100%',
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «consultacuentasxcobrar» en roles para consultar las cuentas
              por cobrar.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cuentas por cobrar"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Antigüedad de cartera${fechaCorteAntiguedad ? ` (corte ${formatearFecha(fechaCorteAntiguedad)})` : ''}`
            : `Antigüedad de cartera${fechaCorteAntiguedad ? ` (corte ${formatearFecha(fechaCorteAntiguedad)})` : ''}`
        }
      />

      <div id="printable-cuentas-cobrar">
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
            <Box sx={filterHeaderSx}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SearchIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                Filtros de cartera
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} className="no-print">
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleBuscar}
                  disabled={cargando}
                  sx={{
                    height: 36,
                    borderColor: FP.color.border,
                    color: FP.color.textPrimary,
                    bgcolor: '#FFFFFF',
                    borderRadius: 1.4,
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                  }}
                >
                  Actualizar
                </Button>
                <PrintListado
                  title={nombreNegocio || 'Empresa'}
                  reportTitle="Listado por antigüedad de saldo — cuentas por cobrar"
                  subtitle={subtituloReporte}
                  columns={columnasPrint}
                  data={ordenadasAntiguedad}
                  emptyMessage="No hay datos para imprimir"
                  printButtonLabel="Imprimir listado por antigüedad"
                  printButtonProps={{
                    sx: {
                      height: 36,
                      textTransform: 'none',
                      borderRadius: 1.4,
                      fontWeight: 700,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    },
                  }}
                  footer={footerPrint}
                />
              </Box>
            </Box>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                sx={{ color: FP.color.textSecondary, fontSize: '0.92rem', lineHeight: 1.5, mb: 2 }}
              >
                Solo se listan facturas a crédito con saldo pendiente. Filtre por fecha de emisión (rango
                completo, solo «hasta» o solo «desde»). El saldo considera pagos, notas de crédito y débito
                vinculadas. Los tramos usan días desde la emisión hasta la fecha «hasta» si la indicó; si no,
                hasta hoy.
              </Typography>

              <Grid container spacing={2.5} alignItems="flex-end" className="no-print">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha emisión desde"
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => setFechaDesde(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Fecha emisión hasta"
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => setFechaHasta(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Solo hasta: deje «desde» vacío"
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={6}>
                  <Box
                    sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center', height: '100%' }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleBuscar}
                      disabled={cargando}
                      sx={{
                        height: 46,
                        bgcolor: FP.color.navy,
                        color: '#FFFFFF',
                        borderRadius: 1.4,
                        textTransform: 'none',
                        fontWeight: 700,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                      }}
                    >
                      Buscar
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleLimpiarFiltros}
                      disabled={cargando}
                      sx={{
                        height: 46,
                        borderColor: FP.color.border,
                        color: FP.color.textPrimary,
                        bgcolor: '#FFFFFF',
                        borderRadius: 1.4,
                        textTransform: 'none',
                        fontWeight: 700,
                        '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                      }}
                    >
                      Limpiar fechas
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {tarjetas.map(({ tramo, cantidad, suma }) => {
              const col = tramoColor(tramo);
              return (
                <Grid item xs={12} sm={6} md={3} key={tramo}>
                  <Card
                    elevation={0}
                    sx={{
                      ...cardShellSx,
                      p: 2,
                      height: '100%',
                      borderLeft: `4px solid ${col.fg}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: 1,
                          bgcolor: col.bg,
                          color: col.fg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <AccountBalanceIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '0.74rem',
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: FP.color.textSecondary,
                        }}
                      >
                        {tramoLabel(tramo)}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.4rem',
                        color: FP.color.textPrimary,
                        fontVariantNumeric: 'tabular-nums',
                        my: 0.5,
                      }}
                    >
                      ${fmtMoney(suma)}
                    </Typography>
                    <Typography sx={{ color: FP.color.textMuted, fontSize: '0.78rem' }}>
                      {cantidad} factura{cantidad !== 1 ? 's' : ''}
                    </Typography>
                  </Card>
                </Grid>
              );
            })}
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Total cartera pendiente"
                value={`$${fmtMoney(totalCartera)}`}
                icon={CarteraIcon}
                accent={FP.color.navy}
              />
            </Grid>
          </Grid>

          <Card elevation={0} sx={cardShellSx}>
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                Detalle de cuentas por cobrar
              </Typography>
              {cargando && (
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                  Cargando…
                </Typography>
              )}
            </Box>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              {cargando ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                  <CircularProgress />
                </Box>
              ) : filas.length === 0 ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                  <CarteraIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                  <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                    No hay datos para mostrar
                  </Typography>
                  <Typography
                    sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}
                    textAlign="center"
                    px={2}
                  >
                    No hay facturas a crédito con saldo pendiente para los filtros indicados.
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    border: `1px solid ${FP.color.border}`,
                    borderRadius: 2,
                    overflow: 'auto',
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableBody>
                      <TableRow
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          '& td': {
                            border: 'none',
                            borderBottom: `2px solid ${FP.color.border}`,
                            bgcolor: FP.color.surfaceAlt,
                          },
                        }}
                      >
                        <TableCell sx={cellHeadSx}>Tramo</TableCell>
                        <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                          Días
                        </TableCell>
                        <TableCell sx={cellHeadSx}>Documento</TableCell>
                        <TableCell sx={cellHeadSx}>Nº</TableCell>
                        <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                          Fecha
                        </TableCell>
                        <TableCell sx={cellHeadSx}>Cliente</TableCell>
                        <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                          Total
                        </TableCell>
                        <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                          Pagado
                        </TableCell>
                        <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                          N/C
                        </TableCell>
                        <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                          N/D
                        </TableCell>
                        <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                          Saldo
                        </TableCell>
                        <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                          NCF
                        </TableCell>
                        <TableCell
                          sx={{ ...cellHeadSx, textAlign: 'center' }}
                          align="center"
                          className="no-print"
                        >
                          Acciones
                        </TableCell>
                      </TableRow>
                      {ordenadasAntiguedad.map((f) => {
                        const col = tramoColor(f.tramo_antiguedad);
                        return (
                          <TableRow key={`${f.tipofactura}-${f.nfact}`} hover>
                            <TableCell>
                              <Chip
                                size="small"
                                label={f.tramo_antiguedad}
                                sx={{ bgcolor: col.bg, color: col.fg, fontWeight: 700 }}
                              />
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }}
                            >
                              {f.dias_antiguedad}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.85rem', color: FP.color.textSecondary }}>
                              {f.tipofactura || '-'}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.86rem' }}>
                              {f.nfact}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontSize: '0.85rem', color: FP.color.textPrimary }}
                            >
                              {formatearFecha(f.fecha)}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 220 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.86rem' }}
                              >
                                {nombreClienteCxC(f)}
                              </Typography>
                              {f.nombre_cliente &&
                                String(f.suplidor || '').trim() !== String(f.nombre_cliente || '').trim() && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: FP.color.textMuted, display: 'block', fontSize: '0.72rem' }}
                                  >
                                    Ref. {f.suplidor}
                                  </Typography>
                                )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: '0.86rem',
                                color: FP.color.textPrimary,
                              }}
                            >
                              ${fmtMoney(f.total)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: '0.86rem',
                                color: FP.color.textSecondary,
                              }}
                            >
                              ${fmtMoney(f.valorpagado)}
                            </TableCell>
                            <TableCell align="center">
                              {f.cant_notas_credito > 0 ? (
                                <Tooltip
                                  title={`${f.cant_notas_credito} nota(s) · $${fmtMoney(f.total_notas_credito)}`}
                                >
                                  <Chip
                                    size="small"
                                    label={`${f.cant_notas_credito} · $${fmtMoney(f.total_notas_credito)}`}
                                    sx={{
                                      bgcolor: FP.color.infoSoft,
                                      color: FP.color.info,
                                      fontWeight: 700,
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography sx={{ color: FP.color.textMuted }}>—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {f.cant_notas_debito > 0 ? (
                                <Tooltip
                                  title={`${f.cant_notas_debito} nota(s) · $${fmtMoney(f.total_notas_debito)}`}
                                >
                                  <Chip
                                    size="small"
                                    label={`${f.cant_notas_debito} · $${fmtMoney(f.total_notas_debito)}`}
                                    sx={{
                                      bgcolor: FP.color.warningSoft,
                                      color: '#92400E',
                                      fontWeight: 700,
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  />
                                </Tooltip>
                              ) : (
                                <Typography sx={{ color: FP.color.textMuted }}>—</Typography>
                              )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 800,
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: '0.92rem',
                                color: FP.color.textPrimary,
                              }}
                            >
                              ${fmtMoney(f.saldo_pendiente)}
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ fontSize: '0.82rem', color: FP.color.textSecondary }}
                            >
                              {f.ncf || '—'}
                            </TableCell>
                            <TableCell align="center" className="no-print">
                              <Tooltip title="Reimprimir factura">
                                <IconButton
                                  size="small"
                                  onClick={() => reimprimirFactura(f.nfact, f.tipofactura)}
                                  sx={{
                                    color: FP.color.accentBlue,
                                    '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                  }}
                                >
                                  <ReceiptIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </div>

      <VistaFactura
        open={vistaFacturaAbierta}
        onClose={() => setVistaFacturaAbierta(false)}
        facturaData={datosVistaFactura}
      />
    </Box>
  );
};

export default CuentasPorCobrar;
